/* ============================================
   MediaPreviewModal — Full-screen file preview before sending
   Shows image/video preview or document icon, allows adding
   a caption, and confirms or cancels sending.
   Inspired by WhatsApp's file preview screen.
   ============================================ */
import { useState, useEffect, useRef, useCallback } from 'react'
import { IconX, IconSend, IconFile } from '@/components/icons/Icons'

interface MediaPreviewModalProps {
  file: File
  onSend: (file: File, caption?: string) => void
  onCancel: () => void
  sending?: boolean
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileExtension(name: string): string {
  const parts = name.split('.')
  return parts.length > 1 ? parts.pop()!.toUpperCase() : ''
}

export default function MediaPreviewModal({ file, onSend, onCancel, sending }: MediaPreviewModalProps) {
  const [caption, setCaption] = useState('')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const captionRef = useRef<HTMLTextAreaElement>(null)
  const isImage = file.type.startsWith('image/')
  const isVideo = file.type.startsWith('video/')
  const ext = getFileExtension(file.name)

  // Generate preview URL for images/videos
  useEffect(() => {
    if (!isImage && !isVideo) return
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file, isImage, isVideo])

  // Focus caption on mount
  useEffect(() => {
    const timeout = setTimeout(() => captionRef.current?.focus(), 100)
    return () => clearTimeout(timeout)
  }, [])

  // Handle Enter to send, Shift+Enter for newline
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
    }
  }

  const handleSend = useCallback(() => {
    if (sending) return
    const trimmed = caption.trim()
    onSend(file, trimmed || undefined)
  }, [file, caption, sending, onSend])

  // ESC to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [onCancel])

  // Prevent backdrop click from propagating
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onCancel()
  }

  return (
    <div className="media-preview-modal" onClick={handleBackdropClick}>
      <div className="media-preview-modal__container">
        {/* Header */}
        <div className="media-preview-modal__header">
          <button
            className="media-preview-modal__close"
            onClick={onCancel}
            type="button"
            title="Cancelar"
          >
            <IconX size={22} />
          </button>
          <div className="media-preview-modal__file-info">
            <span className="media-preview-modal__filename">{file.name}</span>
            <span className="media-preview-modal__filesize">{formatSize(file.size)}</span>
          </div>
        </div>

        {/* Preview Area */}
        <div className="media-preview-modal__preview">
          {isImage && previewUrl ? (
            <img
              src={previewUrl}
              alt={file.name}
              className="media-preview-modal__image"
            />
          ) : isVideo && previewUrl ? (
            <video
              src={previewUrl}
              className="media-preview-modal__video"
              controls
              autoPlay
              muted
            />
          ) : (
            <div className="media-preview-modal__doc">
              <div className="media-preview-modal__doc-icon">
                <IconFile size={56} />
                {ext && <span className="media-preview-modal__doc-ext">{ext}</span>}
              </div>
              <span className="media-preview-modal__doc-name">{file.name}</span>
              <span className="media-preview-modal__doc-size">{formatSize(file.size)}</span>
            </div>
          )}
        </div>

        {/* Caption + Send */}
        <div className="media-preview-modal__footer">
          <div className="media-preview-modal__caption-wrap">
            <textarea
              ref={captionRef}
              className="media-preview-modal__caption"
              placeholder="Agregar un mensaje..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={sending}
            />
          </div>
          <button
            className="media-preview-modal__send"
            onClick={handleSend}
            type="button"
            title="Enviar"
            disabled={sending}
          >
            {sending ? (
              <div className="auth-spinner" style={{ width: 22, height: 22 }} />
            ) : (
              <IconSend size={22} />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
